import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { memo, ReactNode, useMemo, useRef } from 'react';
import { useGamesGridConfig, useGetCombinedGames } from 'react-easyrocket';
import { GAME_CATEGORIES, GameCategoryName } from 'src/constants/gameCategories';
import { CardSeeAll } from 'src/components/Cards/CardSeeAll';
import { GamesGrid, TOTAL_GAMES } from 'src/components/Games/GamesGrid';
import { useRoute } from 'src/router/hooks';
import { useGameCardSize } from 'src/hooks/useGameCardSize';
import { EVENT_NAME } from 'src/config/helpers';

interface GamesBlockProps {
  categoryId: GameCategoryName;
  className?: string;
  totalGames?: number;
  icon?: ReactNode;
}

const _GamesBlock = ({ categoryId, className, totalGames, icon }: GamesBlockProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ROUTE } = useRoute();
  const navigateToAllGamesInCategory = () => navigate(ROUTE.GAMES.get(categoryId));
  const { gamesToShow, columns } = useGamesGridConfig();
  const parentRef = useRef<HTMLDivElement>(null);
  const title = GAME_CATEGORIES[categoryId].title;
  const param = GAME_CATEGORIES[categoryId].param;

  const { games, data, isFetched } = useGetCombinedGames(
    // eslint-disable-next-line camelcase
    { per_page: TOTAL_GAMES },
    { categoryId: param }
  );
  const gameCardSize = useGameCardSize(parentRef.current?.clientWidth, columns);
  const visibleGames = games.slice(0, gamesToShow);
  const totalGamesInCategory = useMemo(() => {
    return data?.pages[0].data.totalCount;
  }, [data]);

  const isEvent = categoryId === EVENT_NAME;

  if (!games.length && isFetched) return null;

  return (
    <div className={className} ref={parentRef}>
      <p className="text-mp_title_game_category items-center gap-2.5 flex flex-row text-lg font-bold uppercase leading-6 py-[20px]">
        {isEvent && icon}
        {t(title)}
      </p>
      <GamesGrid
        isFetching={games.length === 0}
        games={visibleGames}
        isFetched={isFetched}
        gameCardProps={gameCardSize}
        lastItem={
          <CardSeeAll
            onClick={navigateToAllGamesInCategory}
            totalGames={totalGames ? `${totalGames}+` : totalGamesInCategory}
          />
        }
      />
    </div>
  );
};

export const GamesBlock = memo(_GamesBlock);
